import type { Condition, Coding, PlanDefinition } from '@medplum/fhirtypes';
import { CODE_SYSTEMS } from '@liza/fhir-utils';

/**
 * The `useContext[focus]` codings a protocol answers to — the diagnosis codes that make it apply.
 * Empty when the definition declares no focus, which means it matches nothing rather than everything.
 */
export const focusCodingsOf = (planDefinition: PlanDefinition): Coding[] =>
  (planDefinition.useContext ?? [])
    .filter((ctx) => ctx.code?.code === 'focus')
    .flatMap((ctx) => ctx.valueCodeableConcept?.coding ?? []);

/**
 * Whether a coding satisfies a wanted coding. ICD-10-CM is matched as a *prefix*, so `E11` covers the
 * whole category (`E11.9`, `E11.65`, …) the way the guideline means it. Every other system, SNOMED
 * included, is matched exactly: SNOMED is a hierarchy, and treating its codes as prefixes would match
 * unrelated concepts that merely share a numeric stem.
 */
export const codingSatisfies = (candidate: Coding, focus: Coding): boolean => {
  if (candidate.system !== focus.system || !candidate.code || !focus.code) return false;
  return focus.system === CODE_SYSTEMS.ICD10_CM ? candidate.code.startsWith(focus.code) : candidate.code === focus.code;
};

/**
 * Whether a Condition is one this protocol applies to.
 *
 * A Condition routinely carries several codings — an importer's SNOMED alongside our own ICD-10-CM,
 * or two systems from two sources — and any one of them matching is enough. Reading only the first
 * coding, or only one system, is what makes a protocol invisible on a chart that plainly has the
 * diagnosis (#10129).
 */
export const conditionMatches = (condition: Condition, planDefinition: PlanDefinition): boolean => {
  const focus = focusCodingsOf(planDefinition);
  if (focus.length === 0) return false;
  return (condition.code?.coding ?? []).some((candidate) => focus.some((f) => codingSatisfies(candidate, f)));
};

/**
 * The protocols that apply to a Condition, in the order given. A Condition can match more than one
 * (a chart coded for both diabetes and hypertension under one topic), so this returns every match
 * rather than the first.
 */
export const matchProtocols = (condition: Condition, planDefinitions: readonly PlanDefinition[]): PlanDefinition[] =>
  planDefinitions.filter((pd) => conditionMatches(condition, pd));

/**
 * Whether a protocol applies to a person rather than to a diagnosis: it declares no `useContext[focus]`,
 * so no Condition ever matches it and nothing links it from a diagnosis. Such a protocol is applied to
 * every patient by an ensure step, gates its actions on demographics (`%ageYears`, `%sex`) and history,
 * and owns a topic found by its canonical alone, addressing no Condition. Prevention is the first.
 */
export const isPersonLevelProtocol = (planDefinition: PlanDefinition): boolean =>
  focusCodingsOf(planDefinition).length === 0;
